import { X } from "lucide-react";
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { uiStore } from "@/editor/store";
import { useStoreSubscribe } from "@/lib/store";
import { resizeObserverBorderBoxSize } from "./resizeObserverGeometry";

interface ScriptsDebugWindowProps {
  onClose: () => void;
}

interface PanelGeometry {
  x: number;
  y: number;
  width: number;
  height: number;
}

const storageKey = "curva:scripts-debug-window";
const viewportMargin = 12;
const minPanelWidth = 320;
const minPanelHeight = 200;

export function ScriptsDebugWindow({ onClose }: ScriptsDebugWindowProps) {
  const logs = useStoreSubscribe(uiStore.scriptDebugLogs);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const logEndRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ pointerId: number; startX: number; startY: number; panelX: number; panelY: number } | undefined>(undefined);
  const [geometry, setGeometry] = useState<PanelGeometry>(() => initialPanelGeometry());

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;
      const borderBox = resizeObserverBorderBoxSize(entry);
      const width = Math.max(minPanelWidth, borderBox.width);
      const height = Math.max(minPanelHeight, borderBox.height);
      setGeometry(current => {
        if (Math.abs(current.width - width) < 0.5 && Math.abs(current.height - height) < 0.5) {
          return current;
        }
        const next = clampPanelGeometry({ ...current, width, height });
        storePanelGeometry(next);
        return next;
      });
    });

    observer.observe(panel);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setGeometry(current => {
        const next = clampPanelGeometry(current);
        storePanelGeometry(next);
        return next;
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ block: "end" });
  }, [logs]);

  function handleHeaderPointerDown(event: ReactPointerEvent<HTMLDivElement>): void {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      panelX: geometry.x,
      panelY: geometry.y,
    };
  }

  function handleHeaderPointerMove(event: ReactPointerEvent<HTMLDivElement>): void {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const next = clampPanelGeometry({
      ...geometry,
      x: drag.panelX + event.clientX - drag.startX,
      y: drag.panelY + event.clientY - drag.startY,
    });
    setGeometry(next);
  }

  function handleHeaderPointerUp(event: ReactPointerEvent<HTMLDivElement>): void {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = undefined;
    storePanelGeometry(geometry);
  }

  return (
    <section
      ref={panelRef}
      className="fixed z-[1000] flex min-h-[200px] min-w-[320px] resize overflow-hidden rounded-md border border-white/10 bg-[#171b20] shadow-2xl shadow-black/45"
      style={{ left: geometry.x, top: geometry.y, width: geometry.width, height: geometry.height }}
      aria-label="Scripts debug window"
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div
          className="flex h-8 shrink-0 cursor-move items-center justify-between border-b border-white/10 bg-[#20262d] px-2 text-[12px] font-medium text-slate-300"
          onPointerDown={handleHeaderPointerDown}
          onPointerMove={handleHeaderPointerMove}
          onPointerUp={handleHeaderPointerUp}
          onPointerCancel={handleHeaderPointerUp}
        >
          <span className="truncate pr-2">Scripts debug</span>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              className="rounded px-1.5 py-0.5 text-[11px] text-slate-400 hover:bg-white/[0.08] hover:text-slate-100"
              onPointerDown={event => event.stopPropagation()}
              onClick={() => uiStore.clearScriptDebugLogs()}
            >
              Clear
            </button>
            <button
              type="button"
              className="flex size-6 items-center justify-center rounded text-slate-400 hover:bg-white/[0.08] hover:text-slate-100"
              aria-label="Hide scripts debug window"
              onPointerDown={event => event.stopPropagation()}
              onClick={onClose}
            >
              <X className="size-3.5" />
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-auto bg-[#0f1318] px-2 py-2 font-mono text-[12px] leading-5">
          {logs.length === 0 ? (
            <div className="text-slate-500">No script output yet. Run an attached frame script.</div>
          ) : (
            logs.map(entry => (
              <div
                key={entry.id}
                className={
                  entry.level === "error"
                    ? "whitespace-pre-wrap break-words text-rose-300"
                    : entry.level === "info"
                      ? "whitespace-pre-wrap break-words text-sky-300/90"
                      : "whitespace-pre-wrap break-words text-slate-200"
                }
              >
                {entry.source ? (
                  <span className="text-slate-500">[{entry.source}] </span>
                ) : null}
                {entry.message}
              </div>
            ))
          )}
          <div ref={logEndRef} />
        </div>
      </div>
    </section>
  );
}

function initialPanelGeometry(): PanelGeometry {
  const stored = readStoredPanelGeometry();
  if (stored) {
    return clampPanelGeometry(stored);
  }
  const width = Math.min(420, Math.max(minPanelWidth, window.innerWidth * 0.32));
  const height = Math.min(280, Math.max(minPanelHeight, window.innerHeight * 0.35));
  return clampPanelGeometry({
    x: Math.max(viewportMargin, 48),
    y: Math.max(viewportMargin, window.innerHeight - height - 48),
    width,
    height,
  });
}

function clampPanelGeometry(geometry: PanelGeometry): PanelGeometry {
  const width = Math.min(Math.max(minPanelWidth, geometry.width), Math.max(minPanelWidth, window.innerWidth - viewportMargin * 2));
  const height = Math.min(Math.max(minPanelHeight, geometry.height), Math.max(minPanelHeight, window.innerHeight - viewportMargin * 2));
  return {
    width,
    height,
    x: Math.min(Math.max(viewportMargin, geometry.x), Math.max(viewportMargin, window.innerWidth - width - viewportMargin)),
    y: Math.min(Math.max(viewportMargin, geometry.y), Math.max(viewportMargin, window.innerHeight - height - viewportMargin)),
  };
}

function readStoredPanelGeometry(): PanelGeometry | undefined {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<PanelGeometry>;
    if (![parsed.x, parsed.y, parsed.width, parsed.height].every(value => typeof value === "number" && Number.isFinite(value))) {
      return undefined;
    }
    return parsed as PanelGeometry;
  } catch {
    return undefined;
  }
}

function storePanelGeometry(geometry: PanelGeometry): void {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(geometry));
  } catch {
    // Ignore quota / private mode failures.
  }
}
